import React from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';

export type InvoiceChartRow = { label: string; count: number; totalAmount: number };

type Props = {
    rows: InvoiceChartRow[];
    formatAmount: (n: number) => string;
    // Header of the first column in the table view, and what the x-axis holds.
    labelHeader: string;
    // Clients: biggest by dollars first, the top 12 shown and the rest folded
    // into "Other". Off for periods, which keep their (chronological) order.
    foldOther?: boolean;
    emptyText?: string;
};

// Two series, validated together (dataviz palette slots 1-2: blue / orange,
// all checks pass incl. colour-blind separation). Identity is never colour
// alone - each axis is titled with its series' swatch, and the legend names both.
const COUNT_COLOR = '#2a78d6';
const AMOUNT_COLOR = '#eb6834';
const MAX_POINTS = 12;
// Below this many px per point, only every nth x label is drawn.
const MIN_LABEL_GAP = 44;
const HEIGHT = 240;
// The dollar axis needs the wider side ($4.0m vs 40).
const COUNT_SIDE = 44;
const AMOUNT_SIDE = 64;
const MARGIN_Y = { top: 16, bottom: 56 };

// A period with no invoices at all - no dot, and the lines break across it
// rather than dipping to zero.
const isEmptyPoint = (p: InvoiceChartRow) => p.count === 0 && p.totalAmount === 0;

// A round step (1/2/5 x 10^n) giving about `count` ticks from 0 to max.
const niceTicks = (max: number, count = 4): number[] => {
    if (max <= 0) return [0, 1];
    const raw = max / count;
    const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
    const step = [1, 2, 5, 10].map(m => m * magnitude).find(s => s >= raw) || raw;
    const top = Math.ceil(max / step) * step;
    const ticks: number[] = [];
    for (let v = 0; v <= top + step / 2; v += step) ticks.push(Math.round(v * 1e6) / 1e6);
    return ticks;
};

const compactMoney = (n: number) => {
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(abs >= 1e7 ? 0 : 1)}m`;
    if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}k`;
    return `${sign}$${Math.round(abs)}`;
};

export type InvoicePeriodRow = { period: string; count: number; totalAmount: number };
export type PeriodGrain = 'month' | 'quarter' | 'year';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Monthly rows ("YYYY-MM", from invoice-controller.js's periodBreakdown) rolled
// up to the chosen grain (calendar quarters/years), with every period between
// the first and last filled in - an empty month is a real zero (the chart
// leaves a gap there rather than drawing across it). minMonths: always cover
// at least that many months up to the current one, however recent the data.
export const rollUpPeriods = (rows: InvoicePeriodRow[], grain: PeriodGrain, minMonths = 0): InvoiceChartRow[] => {
    if (!rows.length) return [];
    const keyOf = (year: number, month: number) => grain === 'year' ? `${year}` : grain === 'quarter' ? `${year}-Q${Math.floor(month / 3) + 1}` : `${year}-${month}`;
    const labelOf = (year: number, month: number) => {
        const yy = `'${String(year).slice(2)}`;
        if (grain === 'year') return String(year);
        if (grain === 'quarter') return `Q${Math.floor(month / 3) + 1} ${yy}`;
        return `${MONTHS[month]} ${yy}`;
    };
    const parse = (period: string) => { const [y, m] = period.split('-').map(Number); return { year: y, month: m - 1 }; };
    const totals = new Map<string, { count: number; totalAmount: number }>();
    for (const r of rows) {
        const { year, month } = parse(r.period);
        const k = keyOf(year, month);
        const t = totals.get(k) || { count: 0, totalAmount: 0 };
        t.count += r.count;
        t.totalAmount += r.totalAmount;
        totals.set(k, t);
    }
    let first = parse(rows[0].period);
    let last = parse(rows[rows.length - 1].period);
    if (minMonths > 0) {
        const now = new Date();
        const current = { year: now.getFullYear(), month: now.getMonth() };
        const earliest = new Date(now.getFullYear(), now.getMonth() - (minMonths - 1), 1);
        const from = { year: earliest.getFullYear(), month: earliest.getMonth() };
        const before = (a: { year: number; month: number }, b: { year: number; month: number }) => a.year < b.year || (a.year === b.year && a.month < b.month);
        if (before(from, first)) first = from;
        if (before(last, current)) last = current;
    }
    const out: InvoiceChartRow[] = [];
    const seen = new Set<string>();
    for (let y = first.year, m = first.month; y < last.year || (y === last.year && m <= last.month); m === 11 ? (y++, m = 0) : m++) {
        const k = keyOf(y, m);
        if (seen.has(k)) continue;
        seen.add(k);
        out.push({ label: labelOf(y, m), ...(totals.get(k) || { count: 0, totalAmount: 0 }) });
    }
    return out;
};

const truncate = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

// Number of invoices and total dollars as two lines on two scales (dollars on
// the left axis, invoices on the right, each can be turned off from the legend) - per period on the Invoices page, per
// client on the Clients page. Hovering a point shows both values; "View as
// table" lists every row. Drawn by hand like the Documents table's page chart.
const InvoiceChart: React.FC<Props> = ({ rows, formatAmount, labelHeader, foldOther = false, emptyText = 'No invoices match the current filters.' }) => {
    // Dollars on the left axis, invoice count on the right.
    const MARGIN = { ...MARGIN_Y, left: AMOUNT_SIDE, right: COUNT_SIDE };
    // Each series can be turned off from the legend - never both at once.
    const [showAmount, setShowAmount] = React.useState(true);
    const [showCount, setShowCount] = React.useState(true);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [width, setWidth] = React.useState(0);
    const [hover, setHover] = React.useState<number | null>(null);
    const [asTable, setAsTable] = React.useState(false);

    const isEmpty = rows.length === 0;
    React.useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const observer = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
        observer.observe(el);
        return () => observer.disconnect();
        // Re-attach whenever the chart's container comes back (after the
        // empty state or the table view).
    }, [asTable, isEmpty]);

    const points = React.useMemo(() => {
        if (!foldOther) return rows;
        const sorted = [...rows].sort((a, b) => b.totalAmount - a.totalAmount);
        if (sorted.length <= MAX_POINTS) return sorted;
        const rest = sorted.slice(MAX_POINTS - 1);
        return [
            ...sorted.slice(0, MAX_POINTS - 1),
            {
                label: `Other (${rest.length})`,
                count: rest.reduce((s, r) => s + r.count, 0),
                totalAmount: rest.reduce((s, r) => s + r.totalAmount, 0),
            },
        ];
    }, [rows, foldOther]);

    if (!rows.length) {
        return (
            <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', textAlign: 'center', py: 4 }}>
                {emptyText}
            </Typography>
        );
    }

    const plotW = Math.max(0, width - MARGIN.left - MARGIN.right);
    const plotH = HEIGHT - MARGIN.top - MARGIN.bottom;
    const countTicks = niceTicks(Math.max(...points.map(p => p.count)));
    const minAmount = Math.min(0, ...points.map(p => p.totalAmount));
    const amountTicksPos = niceTicks(Math.max(...points.map(p => p.totalAmount), 0));
    const amountTicks = minAmount < 0 ? [...niceTicks(-minAmount).slice(1).map(v => -v).reverse(), ...amountTicksPos] : amountTicksPos;
    const countMax = countTicks[countTicks.length - 1];
    const amountMin = amountTicks[0];
    const amountMax = amountTicks[amountTicks.length - 1];

    const step = points.length > 1 ? plotW / (points.length - 1) : 0;
    const xAt = (i: number) => MARGIN.left + (points.length > 1 ? i * step : plotW / 2);
    const yCount = (v: number) => MARGIN.top + plotH - (v / countMax) * plotH;
    const yAmount = (v: number) => MARGIN.top + plotH - ((v - amountMin) / (amountMax - amountMin || 1)) * plotH;
    // A new segment starts after every empty period, so the line breaks there.
    const path = (y: (v: number) => number, key: 'count' | 'totalAmount') =>
        points.map((p, i) => {
            if (isEmptyPoint(p)) return '';
            const continues = i > 0 && !isEmptyPoint(points[i - 1]);
            return `${continues ? 'L' : 'M'}${xAt(i)},${y(p[key])}`;
        }).filter(Boolean).join(' ');

    // Dollars on the left, count on the right - an axis only while its series is shown.
    const left = { show: showAmount, ticks: amountTicks, y: yAmount, text: compactMoney, color: AMOUNT_COLOR, title: 'Dollars' };
    const right = { show: showCount, ticks: countTicks, y: yCount, text: (t: number) => String(t), color: COUNT_COLOR, title: 'Invoices' };
    // Gridlines follow the left scale, or the right one when dollars are off.
    const grid = left.show ? left : right;
    const bandW = points.length > 1 ? step : plotW;
    const labelEvery = points.length > 1 ? Math.max(1, Math.ceil(MIN_LABEL_GAP / step)) : 1;
    const labelChars = Math.max(6, Math.floor((points.length > 1 ? step * labelEvery : plotW) / 6));
    const tableRows = foldOther ? [...rows].sort((a, b) => b.totalAmount - a.totalAmount) : rows;

    const swatch = (color: string) => (
        <Box component="span" sx={{ display: 'inline-block', width: 14, height: 2, background: color, borderRadius: 1, verticalAlign: 'middle', mr: 0.75 }} />
    );
    const hovered = hover != null ? points[hover] : null;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                {/* The legend doubles as the series toggles - a checkbox per line,
                    in its colour. At least one stays on. */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {[
                        { label: 'Total dollars (left axis)', color: AMOUNT_COLOR, on: showAmount, toggle: () => setShowAmount(v => !v), other: showCount },
                        { label: 'Number of invoices (right axis)', color: COUNT_COLOR, on: showCount, toggle: () => setShowCount(v => !v), other: showAmount },
                    ].map(item => (
                        <FormControlLabel
                            key={item.label}
                            title={item.on && !item.other ? 'At least one line stays on' : ''}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={item.on}
                                    disabled={item.on && !item.other}
                                    onChange={item.toggle}
                                    sx={{ p: 0.5, color: item.color, '&.Mui-checked': { color: item.color }, '&.Mui-disabled.Mui-checked': { color: item.color, opacity: 0.6 } }}
                                />
                            }
                            label={<Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{item.label}</Typography>}
                            sx={{ m: 0, mr: 0.5 }}
                        />
                    ))}
                </Box>
                <Typography
                    onClick={() => setAsTable(v => !v)}
                    sx={{ fontSize: 12, color: 'var(--color-primary)', cursor: 'pointer', textDecoration: 'underline' }}
                >
                    {asTable ? 'View as chart' : 'View as table'}
                </Typography>
            </Box>

            {asTable ? (
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, '& td, & th': { py: 0.5, px: 1, borderBottom: '1px solid rgba(0,0,0,0.08)' } }}>
                    <thead>
                        <tr>
                            <Box component="th" sx={{ textAlign: 'left', color: 'var(--color-text-muted)', fontWeight: 600 }}>{labelHeader}</Box>
                            <Box component="th" sx={{ textAlign: 'right', color: 'var(--color-text-muted)', fontWeight: 600 }}>Invoices</Box>
                            <Box component="th" sx={{ textAlign: 'right', color: 'var(--color-text-muted)', fontWeight: 600 }}>Total</Box>
                        </tr>
                    </thead>
                    <tbody>
                        {tableRows.map(r => (
                            <tr key={r.label}>
                                <td>{r.label}</td>
                                <Box component="td" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.count}</Box>
                                <Box component="td" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatAmount(r.totalAmount)}</Box>
                            </tr>
                        ))}
                    </tbody>
                </Box>
            ) : (
                <Box ref={containerRef} sx={{ position: 'relative', width: '100%', height: HEIGHT }} onMouseLeave={() => setHover(null)}>
                    {width > 0 && (
                        <svg width={width} height={HEIGHT} role="img" aria-label={`Number of invoices and total dollars per ${labelHeader.toLowerCase()}`}>
                            {/* Recessive grid, from one scale only. */}
                            {grid.ticks.map(t => (
                                <line key={`g${t}`} x1={MARGIN.left} x2={MARGIN.left + plotW} y1={grid.y(t)} y2={grid.y(t)} stroke="rgba(0,0,0,0.07)" />
                            ))}
                            {left.show && left.ticks.map(t => (
                                <text key={`l${t}`} x={MARGIN.left - 8} y={left.y(t)} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="var(--color-text-muted)">{left.text(t)}</text>
                            ))}
                            {right.show && right.ticks.map(t => (
                                <text key={`r${t}`} x={MARGIN.left + plotW + 8} y={right.y(t)} dominantBaseline="middle" fontSize={11} fill="var(--color-text-muted)">{right.text(t)}</text>
                            ))}
                            {/* Axis titles, each carrying its series' colour as a swatch. */}
                            {left.show && (
                                <g transform={`translate(12, ${MARGIN.top + plotH / 2}) rotate(-90)`}>
                                    <rect x={-36} y={-5} width={10} height={2} fill={left.color} />
                                    <text x={-22} y={0} fontSize={11} fill="var(--color-text-muted)">{left.title}</text>
                                </g>
                            )}
                            {right.show && (
                                <g transform={`translate(${width - 6}, ${MARGIN.top + plotH / 2}) rotate(90)`}>
                                    <rect x={-30} y={-5} width={10} height={2} fill={right.color} />
                                    <text x={-16} y={0} fontSize={11} fill="var(--color-text-muted)">{right.title}</text>
                                </g>
                            )}

                            {/* Crosshair on the hovered point. */}
                            {hover != null && (
                                <line x1={xAt(hover)} x2={xAt(hover)} y1={MARGIN.top} y2={MARGIN.top + plotH} stroke="rgba(0,0,0,0.25)" strokeDasharray="3 3" />
                            )}

                            {showCount && <path d={path(yCount, 'count')} fill="none" stroke={COUNT_COLOR} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}
                            {showAmount && <path d={path(yAmount, 'totalAmount')} fill="none" stroke={AMOUNT_COLOR} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}
                            {points.map((p, i) => (
                                <g key={p.label}>
                                    {!isEmptyPoint(p) && <>
                                        {showCount && <circle cx={xAt(i)} cy={yCount(p.count)} r={hover === i ? 5 : 4} fill={COUNT_COLOR} stroke="var(--color-page-background, #fff)" strokeWidth={2} />}
                                        {showAmount && <circle cx={xAt(i)} cy={yAmount(p.totalAmount)} r={hover === i ? 5 : 4} fill={AMOUNT_COLOR} stroke="var(--color-page-background, #fff)" strokeWidth={2} />}
                                    </>}
                                    {(i % labelEvery === 0 || hover === i) && <text
                                        x={xAt(i)}
                                        y={MARGIN.top + plotH + 14}
                                        fontSize={11}
                                        fill={hover === i ? 'var(--color-default-text)' : 'var(--color-text-muted)'}
                                        textAnchor="end"
                                        transform={`rotate(-30, ${xAt(i)}, ${MARGIN.top + plotH + 14})`}
                                    >
                                        {truncate(p.label, Math.min(labelChars, 16))}
                                    </text>}
                                    {/* Hit band - the whole column for this point, bigger than the dots. */}
                                    <rect
                                        x={xAt(i) - bandW / 2}
                                        y={MARGIN.top}
                                        width={bandW}
                                        height={plotH}
                                        fill="transparent"
                                        onMouseEnter={() => setHover(i)}
                                    />
                                </g>
                            ))}
                            <line x1={MARGIN.left} x2={MARGIN.left + plotW} y1={MARGIN.top + plotH} y2={MARGIN.top + plotH} stroke="rgba(0,0,0,0.2)" />
                        </svg>
                    )}
                    {hovered && hover != null && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: Math.min(Math.max(xAt(hover) + 12, 0), Math.max(width - 200, 0)),
                                width: 188,
                                pointerEvents: 'none',
                                background: '#fff',
                                border: '1px solid #e9e9e9',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                borderRadius: 1.5,
                                p: 1,
                                fontSize: 12,
                            }}
                        >
                            <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>{hovered.label}</Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{swatch(COUNT_COLOR)}Invoices</span>
                                <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{hovered.count}</strong>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{swatch(AMOUNT_COLOR)}Total</span>
                                <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(hovered.totalAmount)}</strong>
                            </Box>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default InvoiceChart;
